import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { MenuItem, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { searchGlobalContent } from '../../../api';
import { buildQueryString } from '../../../helpers';
import { Portal } from '../../../helpers/bodyPortal';
import useDebounce from '../../../helpers/useDebounce';
import { PATHS } from '../../../enums/PATHS';
import { VIDEOFEED_TYPE } from '../../../enums/ENUMS';
import SearchInput from '../../../components/modules/SearchInput';
import FileManagementIcon from '../../../assets/icons/folder-open.svg';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import styles from './GlobalSearch.module.scss';

const GLOBAL_SEARCH_ICON_STYLE = { width: 18, height: 18, flexShrink: 0, pointerEvents: 'none' as const };

type GlobalSearchSource = 'my_folders' | 'shared_with_me' | 'parent_folders' | 'online_composers';

type GlobalSearchResult = {
  id: number;
  itemType: 'folder' | 'videofeed';
  source: GlobalSearchSource;
  title: string;
  collectionName?: string | null;
  refName?: string | null;
  folderId?: number | null;
  folderName?: string | null;
  path?: string[];
  videoType?: VIDEOFEED_TYPE | string | null;
  videoId?: number | null;
  shared?: boolean;
};

const GLOBAL_SEARCH_SOURCE_LABELS: Record<GlobalSearchSource, string> = {
  my_folders: PATHS.MY_FOLDERS.label,
  shared_with_me: PATHS.SHARED_WITH_ME.label,
  parent_folders: PATHS.COLLABORATIVE_FOLDERS.label,
  online_composers: PATHS.MY_REFLEX_COMPOSERS.label,
};

type GlobalSearchProps = {
  compact?: boolean;
  placement?: 'right' | 'bottom';
  className?: string;
  allContent?: boolean;
  expandOnHover?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ compact = false, placement = 'right', className, allContent = false, expandOnHover = false, onExpandedChange }) => {
  const history = useHistory();
  const location = useLocation();
  const [globalSearch, setGlobalSearch] = useState('');
  const debouncedGlobalSearch = useDebounce(globalSearch, 300);
  const [globalSearchResults, setGlobalSearchResults] = useState<GlobalSearchResult[]>([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hoveredRef = useRef(false);
  const expandable = expandOnHover && !compact;
  const globalSearchWrapRef = useRef<HTMLDivElement>(null);
  const globalSearchMenuRef = useRef<HTMLDivElement>(null);
  const globalSearchAbortRef = useRef<AbortController | null>(null);
  const [globalSearchPanelPosition, setGlobalSearchPanelPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    setExpanded(false);
    setGlobalSearchOpen(false);
    setGlobalSearch('');
    setGlobalSearchResults([]);
  }, [location.pathname, location.search, compact, allContent]);

  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  const collapseIfIdle = (nextFocus: EventTarget | null = document.activeElement) => {
    if (expandable && !hoveredRef.current && !globalSearch && !globalSearchOpen &&
      !globalSearchWrapRef.current?.contains(nextFocus as Node | null) &&
      !globalSearchMenuRef.current?.contains(nextFocus as Node | null)) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (!globalSearchOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!globalSearchWrapRef.current?.contains(target) && !globalSearchMenuRef.current?.contains(target)) {
        setGlobalSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [globalSearchOpen]);

  useEffect(() => {
    if (!globalSearchOpen) {
      setGlobalSearchPanelPosition(null);
      return;
    }
    const anchor = globalSearchWrapRef.current;
    if (!anchor) return;
    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      const margin = 12;
      const gap = 8;
      const width = Math.min(360, window.innerWidth - margin * 2);
      const fitsRight = placement === 'right' && rect.right + gap + width <= window.innerWidth - margin;
      const top = Math.max(margin, fitsRight ? rect.top : rect.bottom + gap);
      setGlobalSearchPanelPosition({
        top,
        left: fitsRight ? rect.right + gap : Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        width,
        maxHeight: Math.max(0, window.innerHeight - top - margin),
      });
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(anchor);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [globalSearchOpen, placement]);

  const buildGlobalSearchResultUrl = (result: GlobalSearchResult) => {
    const sourceLabel = allContent ? 'Content Admin' : GLOBAL_SEARCH_SOURCE_LABELS[result.source];

    if (result.itemType === 'folder') {
      const folderPath =
        allContent
          ? PATHS.ALL_CONTENT_VIDEOFEEDS.path
          : result.source === 'shared_with_me'
            ? PATHS.SHARED_VIDEOFEEDS_LIST.path
            : result.source === 'parent_folders'
              ? PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path
              : PATHS.VIDEOFEEDS_LIST.path;

      return `${folderPath}?${buildQueryString({
        folderId: String(result.id),
        parentPage: sourceLabel,
      })}`;
    }

    const pageType = (result.videoType || VIDEOFEED_TYPE.TIMELINE) as VIDEOFEED_TYPE;
    const baseParams: Record<string, string> = {
      feedId: String(result.id),
      fileside: 'true',
      feed: '0',
      clipId: String(result.videoId || 0),
      clip: '0',
      parentPage: sourceLabel,
      pageType,
    };

    if (result.folderId) {
      baseParams.folderId = String(result.folderId);
    }

    if (allContent) {
      baseParams.allContent = 'true';
      baseParams.shared = 'true';
    } else {
      if (result.source === 'parent_folders') {
        baseParams.parentFolders = 'true';
      }
      if (result.source === 'shared_with_me') {
        baseParams.shared = 'true';
        if (pageType === VIDEOFEED_TYPE.MY_COMPOSER) {
          baseParams.sharedComposer = 'true';
        }
      }
    }

    const videoPath =
      pageType === VIDEOFEED_TYPE.MY_COMPOSER || pageType === VIDEOFEED_TYPE.COMPOSER ? PATHS.COMPOSER_PAGE.path : PATHS.TIMELINE.path;

    return `${videoPath}?${buildQueryString(baseParams)}`;
  };

  const renderGlobalSearchResultIcon = (result: GlobalSearchResult) => {
    const normalizedVideoType = String(result.videoType || '').toLowerCase();
    const isComposerType = normalizedVideoType.includes('composer');
    const isMyComposerType = normalizedVideoType === VIDEOFEED_TYPE.MY_COMPOSER.toLowerCase();
    const isReferenceTimeline = Boolean(
      result.itemType === 'videofeed' && result.collectionName && result.refName && result.collectionName !== result.refName,
    );

    if (result.itemType === 'folder') {
      return <img src={FileManagementIcon} alt="folder" style={GLOBAL_SEARCH_ICON_STYLE} />;
    }

    if (isComposerType) {
      return (
        <ComposerIcon
          style={{
            ...GLOBAL_SEARCH_ICON_STYLE,
            color: isMyComposerType ? '#39b3ea' : undefined,
          }}
        />
      );
    }

    if (isReferenceTimeline) {
      return <TimelineIcon style={{ ...GLOBAL_SEARCH_ICON_STYLE, color: '#39b3ea' }} />;
    }

    return <MovieIcon style={GLOBAL_SEARCH_ICON_STYLE} />;
  };

  const handleGlobalSearchResultClick = (result: GlobalSearchResult) => {
    setGlobalSearch('');
    setGlobalSearchResults([]);
    setGlobalSearchOpen(false);
    history.push(buildGlobalSearchResultUrl(result));
  };

  useEffect(() => {
    const searchTerm = debouncedGlobalSearch.trim();

    if (globalSearchAbortRef.current) {
      globalSearchAbortRef.current.abort();
      globalSearchAbortRef.current = null;
    }

    if (searchTerm.length < 2) {
      setGlobalSearchResults([]);
      setGlobalSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    globalSearchAbortRef.current = controller;
    setGlobalSearchLoading(true);

    void searchGlobalContent(searchTerm, 10, controller.signal, allContent)
      .then((res) => {
        if (controller.signal.aborted) {
          return;
        }
        setGlobalSearchResults(res?.documents || []);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error(error);
          setGlobalSearchResults([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setGlobalSearchLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [debouncedGlobalSearch, allContent]);

  const renderInput = (autoFocus = false) => (
    <div className={styles.inputWrap}>
      <SearchInput
        className={styles.input}
        placeHolder="Global Search"
        aria-label="Global Search"
        autoFocus={autoFocus}
        onClick={expandable ? () => globalSearchWrapRef.current?.querySelector<HTMLInputElement>('input')?.focus() : undefined}
        value={globalSearch}
        onChange={(event) => {
          const value = event.target.value;
          setGlobalSearch(value);
          setGlobalSearchOpen(compact || value.trim().length >= 2);
          if (value.trim().length < 2) setGlobalSearchResults([]);
        }}
        onFocus={() => {
          if (globalSearch.trim().length >= 2) setGlobalSearchOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            setGlobalSearchOpen(false);
            if (expandable) {
              setExpanded(false);
              requestAnimationFrame(() => globalSearchWrapRef.current?.querySelector<HTMLButtonElement>('button')?.focus());
            }
            if (compact) globalSearchWrapRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
          }
        }}
      />
      {globalSearch && (
        <button type="button" className={styles.clearButton} aria-label="Clear search" onClick={() => {
          setGlobalSearch('');
          setGlobalSearchResults([]);
          if (!compact) setGlobalSearchOpen(false);
        }}>
          <CloseIcon />
        </button>
      )}
    </div>
  );

  return (
    <div className={cx(compact ? styles.compact : styles.root, expandable && styles.expandable, expandable && expanded && styles.expanded, className)}
      ref={globalSearchWrapRef}
      onMouseEnter={() => { hoveredRef.current = true; if (expandable) setExpanded(true); }}
      onMouseLeave={() => { hoveredRef.current = false; collapseIfIdle(); }}
      onBlurCapture={(event) => collapseIfIdle(event.relatedTarget)}>
      {(compact || expandable) && (
        <Tooltip title={expandable ? '' : 'Global Search'} placement="right">
          <button type="button" className={styles.searchButton} aria-label="Global Search" aria-expanded={expandable ? expanded : globalSearchOpen}
            onClick={() => {
              if (expandable) {
                setExpanded(true);
                requestAnimationFrame(() => globalSearchWrapRef.current?.querySelector<HTMLInputElement>('input')?.focus());
              } else setGlobalSearchOpen((previous) => !previous);
            }}>
            <SearchIcon />
          </button>
        </Tooltip>
      )}
      {!compact && renderInput()}
      {globalSearchOpen && globalSearchPanelPosition && (
        <Portal>
          <div ref={globalSearchMenuRef} className={styles.results} style={globalSearchPanelPosition}>
            {compact && <div className={styles.panelInput}>{renderInput(true)}</div>}
            {globalSearch.trim().length < 2 ? (
              <div className={styles.hint}>Type at least 2 characters to search.</div>
            ) : (
              <>
                {globalSearchLoading ? (
                  <MenuItem disabled style={{ whiteSpace: 'normal' }}>
                    Searching...
                  </MenuItem>
                ) : globalSearchResults.length ? (
                  globalSearchResults.map((result) => {
                    const fullPath = [
                      allContent ? 'Content Admin' : GLOBAL_SEARCH_SOURCE_LABELS[result.source],
                      ...(result.path ?? [result.folderName, result.title].filter(Boolean)),
                    ].join(' / ');
                    return (
                      <MenuItem
                        key={`${result.itemType}-${result.id}`}
                        onClick={() => handleGlobalSearchResultClick(result)}
                        style={{
                          whiteSpace: 'normal',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          padding: '10px 12px',
                        }}
                      >
                        <div style={{ marginTop: '1px' }}>{renderGlobalSearchResultIcon(result)}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', minWidth: 0 }}>
                          <div style={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {result.itemType === 'videofeed' && result.collectionName ? result.collectionName : result.title}
                          </div>
                          {result.itemType === 'videofeed' &&
                            result.collectionName &&
                            result.refName &&
                            result.collectionName !== result.refName && (
                              <div style={{ fontSize: '11px', opacity: 0.72, lineHeight: 1.2 }}>{result.refName}</div>
                            )}
                          <div title={fullPath} style={{ fontSize: '11px', opacity: 0.72, lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                            {fullPath}
                          </div>
                        </div>
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem disabled style={{ whiteSpace: 'normal' }}>
                    No results
                  </MenuItem>
                )}
              </>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default GlobalSearch;
